import { FC } from 'react';

import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useShareLink } from '@hooks/useShareLink';

import { QRCode } from '@components/QRCode/QRCode';
import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const ShareSection: FC<{ connectionInviteUrl: string }> = ({ connectionInviteUrl }) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const url = connectionInviteUrl;

  const handleShareClick = async () => {
    track('Share connection invite URL');
    await shareLink({
      url,
    });
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2 mb-3"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ maxWidth: 'calc(100% - 75px)' }}>
        <div>
          <FontAwesomeIcon name="link" />
        </div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</div>
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Share'}
        </Button>
      </div>
    </div>
  );
};

interface ConnectQrCodePopupContentProps {
  connectionAutoAcceptKey: string | undefined;
  connectionInviteUrl: string | undefined;
  onClose: () => void;
}

interface ConnectQrCodePopupProps extends Omit<
  ConnectQrCodePopupContentProps,
  'connectionAutoAcceptKey' | 'connectionInviteUrl'
> {
  isVisible: boolean;
}

const PopupContent: FC<ConnectQrCodePopupContentProps> = ({
  connectionAutoAcceptKey,
  connectionInviteUrl,
}) => {
  const qrCodeSize = 180;

  return (
    <div>
      {connectionInviteUrl ? (
        <>
          <div className="fs-4 text-center mt-3 mb-2">
            <strong>Connect with me</strong>
          </div>

          <div className="d-flex justify-content-center">
            <div style={{ width: qrCodeSize, height: qrCodeSize }}>
              <QRCode
                value={`${connectionInviteUrl}?autoAcceptKey=${connectionAutoAcceptKey}`}
                style={{ padding: 8 }}
              />
            </div>
          </div>

          <div className="mt-4">
            <ShareSection connectionInviteUrl={connectionInviteUrl} />
            <div className="text-center" style={{ width: '80%', margin: '0 auto' }}>
              <span>Share your invite link with your friends to connect with you.</span>
            </div>
          </div>
        </>
      ) : (
        <p className="text-muted">No invite link available.</p>
      )}
    </div>
  );
};

export const ConnectQrCodePopup: FC<ConnectQrCodePopupProps> = ({ isVisible, ...props }) => {
  const { currentUser } = useCurrentUser();

  return (
    <Popup isVisible={isVisible} onClose={props.onClose} height="full">
      <Popup.Content>
        {isVisible && (
          <PopupContent
            connectionAutoAcceptKey={currentUser?.connectionAutoAcceptKey}
            connectionInviteUrl={currentUser?.connectionInviteUrl}
            {...props}
          />
        )}
      </Popup.Content>
    </Popup>
  );
};
